import { memo } from 'react';

import { cn } from '@/ui/lib/utils';

import './styles.css';
import type { MissionProgressTone, Props } from './types';

// uniform-* ramps: dark-mode.css doesn't invert them, so the dark: overrides apply.
const FILL_CLASSES: Record<MissionProgressTone, string> = {
  active: 'bg-uniform-green-500 dark:bg-uniform-green-400',
  complete: 'bg-uniform-green-600 dark:bg-uniform-green-500',
  inactive: 'bg-uniform-gray-400 dark:bg-uniform-gray-500'
};

const TRACK_CLASS = 'bg-uniform-gray-200 dark:bg-uniform-gray-700';

/** Above this many goal units the bar is continuous; slivers stop being readable. */
export const MAX_SEGMENTS = 10;

const MissionProgressBar = ({ score, goal, tone, label }: Props) => {
  const reached = Math.min(score, goal);
  const segmented = goal > 0 && goal <= MAX_SEGMENTS;
  const percent = goal > 0 ? Math.round((reached / goal) * 100) : 0;

  return (
    <div className="flex items-center gap-3">
      <div
        role="progressbar"
        aria-label={`${label} progress`}
        aria-valuemin={0}
        aria-valuemax={goal}
        aria-valuenow={reached}
        className={cn('flex h-2 max-w-sm flex-1 gap-1', !segmented && 'overflow-hidden rounded')}>
        {segmented ? (
          Array.from({ length: goal }, (_, index) => (
            <div
              key={index}
              data-testid="mission-progress-segment"
              data-filled={index < reached}
              className={cn(
                'h-full flex-1 rounded',
                index < reached ? FILL_CLASSES[tone] : TRACK_CLASS
              )}
            />
          ))
        ) : (
          <div className={cn('h-full flex-1 rounded', TRACK_CLASS)}>
            <div
              data-testid="mission-progress-fill"
              className={cn('h-full rounded transition-[width]', FILL_CLASSES[tone])}
              style={{ width: `${percent}%` }}
            />
          </div>
        )}
      </div>
      <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-uniform-gray-600 dark:text-uniform-gray-400">
        {score} of {goal}
      </span>
    </div>
  );
};

export default memo(MissionProgressBar);
